import React from "react";
import moment from "moment";
import styled, { css, keyframes } from "styled-components";
import { tokens } from "../../../../../public/tokens";
import { MOBILE_BREAKPOINT } from "../../../../library/breakpoints";
import Avatar from "../../../../library/Avatar";
import { GradebookActionButton } from "./PMCGradeBook.styles";

const { colors, typography, globals, effects } = tokens;

const SUMMARY_COUNTERS = [
    { key: "programmes", icon: "books", label: "Programmes" },
    { key: "modules", icon: "book", label: "Modules" },
    { key: "students", icon: "users", label: "Students" },
    { key: "graduates", icon: "graduation-cap", label: "Graduates" },
    { key: "lockedProgrammes", icon: "lock", label: "Locked Programmes" },
];

const VERSION_STATUS_STYLES = {
    active: { background: colors.states.success["100"], color: colors.states.success["500"] },
    inactive: { background: colors.neutral.grey.Alpha["10"], color: colors.text.secondary },
    locked: { background: colors.states.warning["500"], color: colors.neutral.white },
    unlocked: { background: colors.neutral.grey.Alpha["10"], color: colors.text.secondary },
};

const pulse = keyframes({
    "0%, 100%": { opacity: 0.55 },
    "50%": { opacity: 1 },
});

const StyledSummaryStrip = styled.section({
    width: "100%",
    marginBottom: globals["spacing-2"],
    padding: `13px ${globals["spacing-6"]}`,
    background: colors.surfaces["container-background"],
    border: `${globals["spacing-px"]} solid ${colors.surfaces["border-divider"]}`,
    borderRadius: globals["border-radius"],
});

const SummaryStripCard = styled.div({
    width: "100%",
    minHeight: "35px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: globals["spacing-md"],

    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
        alignItems: "flex-start",
        flexDirection: "column",
    },
});

const SummaryStripMain = styled.div({
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: globals["spacing-md"],
    flex: "0 1 auto",

    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
        flexWrap: "wrap",
    },
});

const SummaryStripDivider = styled.span({
    width: globals["spacing-px"],
    height: "35px",
    flex: `0 0 ${globals["spacing-px"]}`,
    background: colors.surfaces["border-divider"],

    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
        display: "none",
    },
});

const VersionInfo = styled.div({
    display: "flex",
    alignItems: "center",
    gap: globals["spacing-2-5"],
    flexShrink: 0,
});

const VersionName = styled.span({
    color: colors.text.primary,
    fontSize: typography.fontSize["xl-18px"],
    fontFamily: typography.fontFamilies["albert-sans"],
    fontWeight: typography.fontWeights.bold,
    lineHeight: "20px",
    whiteSpace: "nowrap",
});

const VersionStatusBadge = styled.span(({ $colors }) => ({
    minHeight: "22px",
    maxHeight: "22px",
    padding: `${globals["spacing-1"]} ${globals["spacing-2"]}`,
    borderRadius: globals["border-radius"],
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: globals["spacing-1"],
    fontFamily: typography.fontFamilies["albert-sans"],
    fontSize: typography.fontSize["md-12px"],
    fontWeight: typography.fontWeights.semibold,
    lineHeight: 1,
    whiteSpace: "nowrap",
    ...$colors,

    i: {
        width: globals["spacing-2-5"],
        fontSize: typography.fontSize["sm-10px"],
        lineHeight: 1,
        textAlign: "center",
        flexShrink: 0,
    },
}));

const QCInfo = styled.div({
    display: "flex",
    alignItems: "center",
    gap: globals["spacing-1-5"],
    flexShrink: 0,
});

const QCDetails = styled.span({
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: globals["spacing-0-5"],
});

const QCName = styled.span({
    color: colors.text.primary,
    fontSize: typography.fontSize["md-12px"],
    fontFamily: typography.fontFamilies["albert-sans"],
    fontWeight: typography.fontWeights.semibold,
    lineHeight: "12px",
});

const QCRole = styled.span({
    color: colors.text.secondary,
    fontSize: typography.fontSize["sm-10px"],
    fontFamily: typography.fontFamilies["albert-sans"],
    fontWeight: typography.fontWeights.regular,
    lineHeight: "12px",
    whiteSpace: "nowrap",
});

const NotAssignedIcon = styled.span({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: "28px",
    height: "28px",
});

const NotAssignedText = styled.span({
    color: colors.text.secondary,
    fontSize: typography.fontSize["md-12px"],
    fontFamily: typography.fontFamilies["albert-sans"],
    fontWeight: typography.fontWeights.regular,
    lineHeight: "12px",
    whiteSpace: "nowrap",
});

const AlertDot = styled.i({
    marginLeft: globals["spacing-1"],
    color: colors.ui.destructive,
    fontSize: "10px",
    lineHeight: 1,
});

const SummaryCounters = styled.div({
    display: "flex",
    alignItems: "center",
    gap: globals["spacing-5"],
    flexShrink: 0,

    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
        flexWrap: "wrap",
    },
});

const SummaryCounter = styled.div({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    flexShrink: 0,
});

const SummaryCounterLabel = styled.span({
    display: "inline-flex",
    alignItems: "center",
    gap: globals["spacing-1-5"],
    color: colors.text.primary,
    fontSize: typography.fontSize["md-12px"],
    fontFamily: typography.fontFamilies["albert-sans"],
    fontWeight: typography.fontWeights.semibold,
    lineHeight: "12px",
    whiteSpace: "nowrap",

    i: {
        width: globals["spacing-3-5"],
        fontSize: typography.fontSize["lg-14px"],
        lineHeight: "14px",
        textAlign: "center",
        flexShrink: 0,
    },
});

const SummaryCounterValueWrapper = styled.span({
    height: globals["spacing-5"],
    paddingLeft: globals["spacing-5"],
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
});

const SummaryCounterValue = styled.span({
    color: colors.text.secondary,
    fontSize: typography.fontSize["md-12px"],
    fontFamily: typography.fontFamilies["albert-sans"],
    fontWeight: typography.fontWeights.regular,
    lineHeight: "12px",
    whiteSpace: "nowrap",
});

const SummaryCounterSkeleton = styled.span({
    width: "33.21px",
    minWidth: "33.21px",
    height: globals["spacing-3"],
    display: "inline-block",
    borderRadius: globals["border-radius"],
    background: `linear-gradient(90deg, ${colors.neutral.grey["100"]} 0%, ${colors.neutral.grey["100"]} 45%, ${colors.neutral.grey["300"]} 100%)`,
    animation: css`${pulse} 1.2s ease-in-out infinite`,
});

const SummaryStripButtons = styled.div({
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: globals["spacing-2"],
    flexShrink: 0,
});

const SummaryStripButton = styled(GradebookActionButton)({
    borderRadius: globals["border-radius"],
    boxShadow: effects.effects.shadow.default,
    flexShrink: 0,
    whiteSpace: "nowrap",

    i: {
        width: globals["spacing-4"],
        fontSize: typography.fontSize["lg-14px"],
        lineHeight: "14px",
        textAlign: "center",
    },
});

const SummaryStripUpdatedRow = styled.div({
    display: "flex",
    justifyContent: "flex-end",
    width: "100%",
    marginBottom: globals["spacing-2"],
});

const SummaryStripUpdatedText = styled.small({
    display: "inline-flex",
    alignItems: "center",
    gap: globals["spacing-1"],
    fontFamily: typography.fontFamilies["albert-sans"],
    fontSize: typography.fontSize["sm-10px"],
    color: colors.text.secondary,
    whiteSpace: "nowrap",
});

/**
 * PMC Gradebook's "Version Summary Strip": version name, Active/Locked badges, the
 * read-only Qualification Co-ordinator, summary counters, the Calculate Final Grades /
 * Download SOR actions, and the last-update line.
 */
function SummaryStrip({
    versionName = "—",
    isActiveVersion = false,
    isVersionLocked = false,
    coordinator,
    totals,
    isLoading = false,
    isCalculating = false,
    isBusy = false,
    isSelecting = false,
    onCalculateFinalGrades,
    onDownloadSOR,
    downloadDisabled = false,
    updatedAt,
    updatedByName,
}) {
    return (
        <>
            <StyledSummaryStrip aria-label="Programme summary">
                <SummaryStripCard>
                    <SummaryStripMain>
                        <VersionInfo>
                            <VersionName>{versionName}</VersionName>

                            <VersionStatusBadge $colors={VERSION_STATUS_STYLES[isActiveVersion ? "active" : "inactive"]}>
                                <i className={`fas fa-${isActiveVersion ? "circle-check" : "times-circle"}`} aria-hidden="true" />
                                {isActiveVersion ? "Active" : "Inactive"}
                            </VersionStatusBadge>

                            <VersionStatusBadge $colors={VERSION_STATUS_STYLES[isVersionLocked ? "locked" : "unlocked"]}>
                                <i className={`fal fa-${isVersionLocked ? "lock" : "unlock"}`} aria-hidden="true" />
                                {isVersionLocked ? "Locked" : "Unlocked"}
                            </VersionStatusBadge>
                        </VersionInfo>

                        <SummaryStripDivider aria-hidden="true" />

                        <QCInfo>
                            {coordinator ? (
                                <>
                                    <Avatar name={coordinator.name} image={coordinator.avatar} />

                                    <QCDetails>
                                        <QCName>{coordinator.name}</QCName>
                                        <QCRole>Qualification Co-ordinator</QCRole>
                                    </QCDetails>
                                </>
                            ) : (
                                <>
                                    <NotAssignedIcon aria-hidden="true">
                                        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <circle cx="14" cy="14" r="14" fill="#E39219" fillOpacity="0.1" />
                                            <path d="M12.3516 12.7328C13.5135 12.7328 14.4641 11.7822 14.4641 10.6203C14.4641 9.45841 13.5135 8.50778 12.3516 8.50778C11.1897 8.50778 10.2391 9.45841 10.2391 10.6203C10.2391 11.7822 11.1897 12.7328 12.3516 12.7328ZM12.3516 7.24028C14.2264 7.24028 15.7316 8.74544 15.7316 10.6203C15.7316 12.4951 14.2264 14.0003 12.3516 14.0003C10.4767 14.0003 8.97156 12.4951 8.97156 10.6203C8.97156 8.74544 10.4767 7.24028 12.3516 7.24028ZM11.0841 16.5353C9.20921 16.5353 7.70405 18.0405 7.70405 19.9153V20.1266C7.70405 20.4699 7.41358 20.7603 7.0703 20.7603C6.72701 20.7603 6.43654 20.4699 6.43654 20.1266V19.9153C6.43654 17.3539 8.52265 15.2678 11.0841 15.2678H13.6191C16.1805 15.2678 18.2666 17.3539 18.2666 19.9153V20.1266C18.2666 20.4699 17.9761 20.7603 17.6329 20.7603C17.2896 20.7603 16.9991 20.4699 16.9991 20.1266V19.9153C16.9991 18.0405 15.4939 16.5353 13.6191 16.5353H11.0841ZM19.5341 10.409C19.1116 10.409 18.7683 10.7523 18.7683 11.1748C18.7683 11.5445 18.4779 11.8086 18.1346 11.8086C17.7913 11.8086 17.5008 11.5445 17.5008 11.1748C17.5008 10.0658 18.3986 9.14154 19.5341 9.14154C20.6696 9.14154 21.5674 10.0658 21.5674 11.1748C21.5674 12.3895 20.6696 12.8912 20.1679 13.0761C20.0886 13.3666 19.851 13.5778 19.5341 13.5778C19.1908 13.5778 18.9004 13.2873 18.9004 12.9441C18.9004 12.4159 19.2965 12.0198 19.6926 11.9142C19.851 11.8614 20.0094 11.7558 20.1151 11.6501C20.2207 11.5445 20.2999 11.4125 20.2999 11.1748C20.2999 10.7523 19.9566 10.409 19.5341 10.409ZM18.7947 14.8453C18.7947 14.4492 19.138 14.1059 19.5341 14.1059C19.9302 14.1059 20.2735 14.4492 20.2735 14.8453C20.2735 15.2414 19.9302 15.5847 19.5341 15.5847C19.138 15.5847 18.7947 15.2414 18.7947 14.8453Z" fill={colors.states.warning["500"]} />
                                        </svg>
                                    </NotAssignedIcon>

                                    <QCDetails>
                                        <NotAssignedText>
                                            Not Assigned
                                            <AlertDot className="fas fa-exclamation-circle" aria-hidden="true" />
                                        </NotAssignedText>
                                    </QCDetails>
                                </>
                            )}
                        </QCInfo>

                        <SummaryStripDivider aria-hidden="true" />

                        <SummaryCounters>
                            {SUMMARY_COUNTERS.map((counter) => (
                                <SummaryCounter key={counter.key}>
                                    <SummaryCounterLabel>
                                        <i className={`fal fa-${counter.icon}`} aria-hidden="true" />
                                        {counter.label}
                                    </SummaryCounterLabel>

                                    <SummaryCounterValueWrapper>
                                        {isLoading ? (
                                            <SummaryCounterSkeleton aria-label={`Loading ${counter.label}`} />
                                        ) : (
                                            <SummaryCounterValue>{totals?.[counter.key] ?? 0}</SummaryCounterValue>
                                        )}
                                    </SummaryCounterValueWrapper>
                                </SummaryCounter>
                            ))}
                        </SummaryCounters>
                    </SummaryStripMain>

                    <SummaryStripButtons>
                        <SummaryStripButton
                            variant="accent"
                            size="lg"
                            type="button"
                            title={
                                isCalculating
                                    ? "Calculation is running"
                                    : isBusy
                                        ? "A calculation is already running"
                                        : isSelecting
                                            ? "Busy selecting students"
                                            : "Calculate Final Grades"
                            }
                            disabled={isBusy || isSelecting}
                            onClick={onCalculateFinalGrades}
                        >
                            <i
                                className={isCalculating ? "fal fa-circle-notch fa-spin" : "fal fa-sync"}
                                style={isCalculating ? { color: colors.brand.blue["700"] } : undefined}
                                aria-hidden="true"
                            />
                            {isCalculating ? "Calculating Grades..." : "Calculate Final Grades"}
                        </SummaryStripButton>

                        <SummaryStripButton
                            variant="primary"
                            size="lg"
                            type="button"
                            title={
                                isBusy
                                    ? "A calculation is already running"
                                    : isSelecting
                                        ? "Busy selecting students"
                                        : "Download Statement of Results"
                            }
                            disabled={isBusy || isSelecting || downloadDisabled}
                            onClick={onDownloadSOR}
                        >
                            <i className="fal fa-download" aria-hidden="true" />
                            Download SOR
                        </SummaryStripButton>
                    </SummaryStripButtons>
                </SummaryStripCard>
            </StyledSummaryStrip>

            {updatedAt && updatedByName && (
                <SummaryStripUpdatedRow>
                    <SummaryStripUpdatedText>
                        <i className="fal fa-history" aria-hidden="true" />
                        Updated <strong>{moment(updatedAt).format('DD MMM YY')}</strong>{" "}
                        at <strong>{moment(updatedAt).format('HH:mm')}</strong>{" "}
                        by <strong>{updatedByName}</strong>
                    </SummaryStripUpdatedText>
                </SummaryStripUpdatedRow>
            )}
        </>
    );
}

export default SummaryStrip;
